// ============================================================
// IE ENGINE — PARTY SYSTEM
// The persistent cross-game party. A party is a social contract,
// not a session — you're in it until you explicitly leave.
//
// LOCKED SCHEMA (Apr 2026 — do not redesign):
//   parties/{partyId}/
//     leader       uid of the current leader
//     members      { [uid]: { name, joinedAt, game, ts } }
//     createdAt    ms timestamp
//     currentGame  "pier" | "terror" | "casino" | ...
//     chat         { [msgId]: { uid, name, text, ts } }
//     readyup      { portalId, label, icon, color, url,
//                    startedBy, startedAt, endsAt, ready: { [uid]: true } }
//   profiles/{uid}/currentPartyId   → pointer to the party
//   partyInvites/{targetUid}        → { fromUid, fromName, partyId, ts }
//   Party size cap = 4. All IE games read ?partyId= from URL.
//
// This file is IDENTICAL across all IE repos (src/engine/).
// Pierre, Terror, and Snake Eyes each mount usePartySystem with
// their own currentGame tag — everything else is shared.
//
// Usage in any IE game:
//   import {
//     usePartySystem, PartyHUD, PartyInviteBanner,
//     ReadyUpModal, SoloInvitePill, PARTY_MAX,
//   } from "./engine/PartySystem";
//
//   const party = usePartySystem({
//     uid, playerName, currentGame: "pier",
//   });
//
//   // In render:
//   <PartyHUD {...party} />
//   <PartyInviteBanner {...party} />
//   {party.readyup && <ReadyUpModal {...party} uid={uid} />}
//   {!party.partyId && <SoloInvitePill />}
//
//   // Inviting (proximity pill, player click, etc.):
//   party.invitePlayer(targetUid, targetName);
//
//   // Leader launching a portal for the whole party:
//   party.startReadyUp({ id, label, icon, color, url });
//
//   // Solo travel while partied — append the party to the URL:
//   const url = withPartyParam(portal.url, uid, party.partyId);
// ============================================================

import { useState, useEffect, useRef, useCallback } from "react";
import {
  ref, set, get, onValue, remove, update, push, runTransaction,
  query, orderByChild, limitToLast,
} from "firebase/database";
import { db } from "./firebase";
import { SD } from "./theme";

// ─────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────
export const PARTY_MAX = 4;                 // locked Apr 2026
const INVITE_TTL_MS    = 60 * 1000;         // invites expire after 60s
const CHAT_TTL_MS      = 6 * 60 * 60 * 1000;// party chat lives 6h
const CHAT_LIMIT       = 50;                // last 50 messages
const READYUP_MS       = 15 * 1000;         // the 15-second countdown
const READYUP_STALE_MS = 30 * 1000;         // readyup older than endsAt+30s = stale
const LOCATION_BEAT_MS = 10 * 1000;         // "where am I" heartbeat

export const GAME_LABELS = {
  pier:   "W'ily's Pier",
  terror: "Terror TCG",
  casino: "Snake Eyes",
  sol:    "Sol Survivor",
  dtd:    "Dusk till Dawn",
};

// ── URL helpers — the cross-game handoff contract ────────────
// Every IE game launches every other IE game through this.
export function withPartyParam(baseUrl, uid, partyId) {
  const u = `${baseUrl}?uid=${encodeURIComponent(uid)}`;
  return partyId ? `${u}&partyId=${encodeURIComponent(partyId)}` : u;
}

// Read ?partyId= from the URL once, then strip it (same manners
// as the ?uid= handoff — read, adopt, replaceState).
function readAndStripUrlPartyId() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const pid = params.get("partyId");
  if (!pid) return null;
  params.delete("partyId");
  const qs = params.toString();
  const clean = window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
  window.history.replaceState({}, "", clean);
  return pid;
}

// ─────────────────────────────────────────────────────────────
// usePartySystem — the whole party lifecycle in one hook
// ─────────────────────────────────────────────────────────────
export function usePartySystem({ uid, playerName, currentGame = "pier" }) {
  const [partyId, setPartyId]   = useState(null);
  const [party, setParty]       = useState(null);   // full parties/{id} node
  const [invite, setInvite]     = useState(null);   // incoming { fromUid, fromName, partyId }
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const launchedRef = useRef(false);
  const urlPartyRef = useRef(undefined); // undefined = not read yet

  // ── 0) Adopt ?partyId= from the URL (arrivals from other games) ──
  // Runs once. If the URL carries a party and our pointer doesn't,
  // verify we're actually a member before adopting (no gatecrashing).
  useEffect(() => {
    if (!uid || urlPartyRef.current !== undefined) return;
    urlPartyRef.current = readAndStripUrlPartyId();
    const pid = urlPartyRef.current;
    if (!pid) return;
    (async () => {
      try {
        const snap = await get(ref(db, `parties/${pid}/members/${uid}`));
        if (snap.exists()) {
          await set(ref(db, `profiles/${uid}/currentPartyId`), pid);
        }
      } catch { /* pointer listener below remains the source of truth */ }
    })();
  }, [uid]);

  // ── 1) Follow the profile pointer ────────────────────────
  useEffect(() => {
    if (!uid) return;
    const unsub = onValue(ref(db, `profiles/${uid}/currentPartyId`), snap => {
      setPartyId(snap.val() || null);
    });
    return () => unsub();
  }, [uid]);

  // ── 2) Follow the party node itself ──────────────────────
  useEffect(() => {
    if (!uid || !partyId) { setParty(null); return; }
    const unsub = onValue(ref(db, `parties/${partyId}`), snap => {
      const p = snap.val();
      if (!p || !p.members || !p.members[uid]) {
        // Party dissolved, or we were removed — clear the pointer.
        remove(ref(db, `profiles/${uid}/currentPartyId`)).catch(() => {});
        setParty(null);
        return;
      }
      setParty(p);
    });
    return () => unsub();
  }, [uid, partyId]);

  // ── 3) Location heartbeat — "where is everyone right now" ─
  // Party persists across disconnects (social contract), so this
  // never *removes* anyone — it only keeps the location fresh.
  useEffect(() => {
    if (!uid || !partyId || !party) return;
    const stamp = () => {
      update(ref(db, `parties/${partyId}/members/${uid}`), {
        game: currentGame, ts: Date.now(),
      }).catch(() => {});
    };
    stamp();
    const t = setInterval(stamp, LOCATION_BEAT_MS);
    return () => clearInterval(t);
  }, [uid, partyId, currentGame, !!party]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 4) Incoming invites ──────────────────────────────────
  useEffect(() => {
    if (!uid) return;
    const invRef = ref(db, `partyInvites/${uid}`);
    const unsub = onValue(invRef, snap => {
      const inv = snap.val();
      if (inv && inv.fromUid && inv.fromUid !== uid && inv.partyId) {
        if (!inv.ts || Date.now() - inv.ts < INVITE_TTL_MS) {
          setInvite(inv);
        } else {
          remove(invRef).catch(() => {});
          setInvite(null);
        }
      } else {
        setInvite(null);
      }
    });
    return () => unsub();
  }, [uid]);

  // ── 5) Party chat listener (history shown — coordination) ─
  useEffect(() => {
    if (!uid || !partyId) { setChatMsgs([]); return; }
    const chatRef = query(
      ref(db, `parties/${partyId}/chat`),
      orderByChild("ts"),
      limitToLast(CHAT_LIMIT)
    );
    const unsub = onValue(chatRef, snap => {
      if (!snap.exists()) { setChatMsgs([]); return; }
      const now = Date.now();
      const msgs = [];
      snap.forEach(child => {
        const m = { id: child.key, ...child.val() };
        if (now - m.ts > CHAT_TTL_MS) {
          remove(ref(db, `parties/${partyId}/chat/${m.id}`)).catch(() => {});
          return;
        }
        msgs.push(m);
      });
      setChatMsgs(msgs);
    });
    return () => unsub();
  }, [uid, partyId]);

  const sendChat = useCallback(async () => {
    const text = chatInput.trim();
    if (!text || !uid || !partyId) return;
    setChatInput("");
    await push(ref(db, `parties/${partyId}/chat`), {
      uid, name: playerName, text, ts: Date.now(),
    }).catch(() => {});
  }, [chatInput, uid, playerName, partyId]);

  // ── 6) Invite a player (creates the party on first invite) ─
  const invitePlayer = useCallback(async (targetUid, targetName) => {
    if (!uid || !targetUid || targetUid === uid) return { ok: false, reason: "bad_target" };

    let pid = partyId;

    // No party yet → forge one, leader = us.
    if (!pid) {
      const newRef = push(ref(db, "parties"));
      pid = newRef.key;
      const now = Date.now();
      await set(newRef, {
        leader: uid,
        createdAt: now,
        currentGame,
        members: {
          [uid]: { name: playerName, joinedAt: now, game: currentGame, ts: now },
        },
      });
      await set(ref(db, `profiles/${uid}/currentPartyId`), pid);
    } else {
      // Existing party — respect the cap before inviting.
      const size = Object.keys(party?.members || {}).length;
      if (size >= PARTY_MAX) return { ok: false, reason: "party_full" };
    }

    await set(ref(db, `partyInvites/${targetUid}`), {
      fromUid: uid, fromName: playerName, partyId: pid, ts: Date.now(),
    });
    return { ok: true, partyId: pid };
  }, [uid, playerName, partyId, party, currentGame]);

  // ── 7) Accept / decline ──────────────────────────────────
  // Accept runs a transaction on members — two players racing for
  // the last slot can't both win. (Delta-transaction discipline.)
  const acceptInvite = useCallback(async () => {
    if (!uid || !invite?.partyId) return { ok: false };
    const pid = invite.partyId;
    remove(ref(db, `partyInvites/${uid}`)).catch(() => {});
    setInvite(null);

    // Already in another party? Leave it first (one party per soul).
    if (partyId && partyId !== pid) await leavePartyInternal(uid, partyId, party);

    const now = Date.now();
    const res = await runTransaction(ref(db, `parties/${pid}/members`), members => {
      if (members === null) return members;                    // party gone → abort
      if (members[uid]) return members;                        // already in — idempotent
      if (Object.keys(members).length >= PARTY_MAX) return;    // full → abort (undefined)
      return { ...members, [uid]: { name: playerName, joinedAt: now, game: currentGame, ts: now } };
    }).catch(() => null);

    const joined = !!res?.committed && !!res?.snapshot?.val()?.[uid];
    if (joined) {
      await set(ref(db, `profiles/${uid}/currentPartyId`), pid);
      return { ok: true };
    }
    return { ok: false, reason: "party_full_or_gone" };
  }, [uid, playerName, invite, partyId, party, currentGame]); // eslint-disable-line react-hooks/exhaustive-deps

  const declineInvite = useCallback(() => {
    if (!uid) return;
    remove(ref(db, `partyInvites/${uid}`)).catch(() => {});
    setInvite(null);
  }, [uid]);

  // ── 8) Leave (with leadership auto-transfer) ─────────────
  // Leader leaves → crown passes to the longest-standing member.
  // Last member leaves → the party node is laid to rest whole.
  async function leavePartyInternal(selfUid, pid, currentParty) {
    const members = currentParty?.members || {};
    const rest = Object.entries(members).filter(([id]) => id !== selfUid);

    if (rest.length === 0) {
      await remove(ref(db, `parties/${pid}`)).catch(() => {});
    } else {
      const updates = { [`parties/${pid}/members/${selfUid}`]: null };
      if (currentParty?.leader === selfUid) {
        rest.sort((a, b) => (a[1].joinedAt || 0) - (b[1].joinedAt || 0));
        updates[`parties/${pid}/leader`] = rest[0][0];
      }
      await update(ref(db), updates).catch(() => {});
    }
    await remove(ref(db, `profiles/${selfUid}/currentPartyId`)).catch(() => {});
  }

  const leaveParty = useCallback(async () => {
    if (!uid || !partyId) return;
    await leavePartyInternal(uid, partyId, party);
    setChatOpen(false);
  }, [uid, partyId, party]);

  // ── 9) Ready-up — the synchronized portal launch ─────────
  const readyup  = party?.readyup || null;
  const isLeader = !!party && party.leader === uid;
  const memberUids = Object.keys(party?.members || {});

  // Stale readyup cleanup (everyone launched last time; nobody was
  // left behind to sweep — first member back sweeps it).
  useEffect(() => {
    if (!partyId || !readyup) return;
    if (readyup.endsAt && Date.now() > readyup.endsAt + READYUP_STALE_MS) {
      remove(ref(db, `parties/${partyId}/readyup`)).catch(() => {});
    }
  }, [partyId, readyup]);

  const startReadyUp = useCallback(async (portal) => {
    if (!uid || !partyId || !isLeader || !portal?.url) return;
    const now = Date.now();
    await set(ref(db, `parties/${partyId}/readyup`), {
      portalId: portal.id, label: portal.label,
      icon: portal.icon || "◆", color: portal.color || SD.gold,
      url: portal.url,
      startedBy: uid, startedAt: now, endsAt: now + READYUP_MS,
      ready: { [uid]: true },
    }).catch(() => {});
  }, [uid, partyId, isLeader]);

  const setReady = useCallback(() => {
    if (!uid || !partyId || !readyup) return;
    set(ref(db, `parties/${partyId}/readyup/ready/${uid}`), true).catch(() => {});
  }, [uid, partyId, readyup]);

  const cancelReadyUp = useCallback(() => {
    if (!partyId || !readyup) return;
    if (readyup.startedBy !== uid && !isLeader) return;
    remove(ref(db, `parties/${partyId}/readyup`)).catch(() => {});
  }, [uid, partyId, readyup, isLeader]);

  // Launch trigger — all ready, or the countdown reaches zero.
  // Each client redirects ITSELF with its own uid + the shared partyId,
  // so everyone lands simultaneously as themselves.
  useEffect(() => {
    if (!uid || !partyId || !readyup || launchedRef.current) return;
    if (!readyup.endsAt || Date.now() > readyup.endsAt + READYUP_STALE_MS) return;

    const allReady = memberUids.length > 0 &&
      memberUids.every(id => readyup.ready && readyup.ready[id]);

    const launch = () => {
      if (launchedRef.current) return;
      launchedRef.current = true;
      // The initiator stamps where the party went before departing.
      if (readyup.startedBy === uid) {
        set(ref(db, `parties/${partyId}/currentGame`), readyup.portalId).catch(() => {});
      }
      window.location.href = withPartyParam(readyup.url, uid, partyId);
    };

    if (allReady) { launch(); return; }
    const msLeft = readyup.endsAt - Date.now();
    if (msLeft <= 0) { launch(); return; }
    const t = setTimeout(launch, msLeft);
    return () => clearTimeout(t);
  }, [uid, partyId, readyup, memberUids]);

  return {
    // state
    partyId, party, invite, isLeader, memberUids, readyup,
    // invites
    invitePlayer, acceptInvite, declineInvite,
    // membership
    leaveParty,
    // chat
    chatMsgs, chatOpen, setChatOpen, chatInput, setChatInput, sendChat,
    // ready-up
    startReadyUp, setReady, cancelReadyUp,
    // context passthrough for the UI components
    uid, currentGame,
  };
}

// ─────────────────────────────────────────────────────────────
// PartyHUD — member roster, locations, chat, leave.
// Fixed top-left, below wherever the host app puts its buttons.
// ─────────────────────────────────────────────────────────────
export function PartyHUD({
  party, partyId, uid, isLeader, leaveParty,
  chatMsgs, chatOpen, setChatOpen, chatInput, setChatInput, sendChat,
  top = 58, left = 14,
  dock = null,   // a band box (railPanelStyle) — the card sits in the backdrop band instead
}) {
  const [unseen, setUnseen] = useState(0);
  const seenCount = useRef(0);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (chatOpen) {
      seenCount.current = chatMsgs.length;
      setUnseen(0);
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    } else {
      setUnseen(Math.max(0, chatMsgs.length - seenCount.current));
    }
  }, [chatMsgs.length, chatOpen]);

  if (!party || !partyId) return null;
  const members = Object.entries(party.members || {})
    .sort((a, b) => (a[1].joinedAt || 0) - (b[1].joinedAt || 0));

  return (
    <div style={dock
      ? { ...dock, zIndex: 64, fontFamily: SD.fontDisplay, overflowY: "auto", boxSizing: "border-box" }
      : { position: "fixed", top, left, zIndex: 160, fontFamily: SD.fontDisplay, width: 190 }}>
      {/* Roster card */}
      <div style={{
        background: "rgba(5,3,20,0.94)",
        border: `1px solid ${SD.purpleLight}55`,
        borderRadius: 10, padding: "8px 10px",
        boxShadow: `0 0 16px ${SD.purpleLight}22`,
      }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginBottom: 6,
        }}>
          <span style={{ fontSize: 9, color: SD.purpleLight, letterSpacing: 2, fontWeight: 900 }}>
            ♥ PARTY {members.length}/{PARTY_MAX}
          </span>
          <span style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setChatOpen(o => !o)} title="Party chat" style={{
              background: "transparent", border: "none", cursor: "pointer",
              fontSize: 12, position: "relative", padding: 0,
            }}>💬
              {unseen > 0 && !chatOpen && (
                <span style={{
                  position: "absolute", top: -5, right: -7,
                  minWidth: 13, height: 13, borderRadius: 7,
                  background: SD.purpleLight, color: "#04020e",
                  fontSize: 8, fontWeight: 900, lineHeight: "13px",
                  padding: "0 2px",
                }}>{unseen > 9 ? "9+" : unseen}</span>
              )}
            </button>
            <button onClick={leaveParty} title="Leave party" style={{
              background: "transparent", border: "none", cursor: "pointer",
              fontSize: 11, color: SD.starDim, padding: 0,
            }}>✕</button>
          </span>
        </div>
        {members.map(([mid, m]) => (
          <div key={mid} style={{
            display: "flex", alignItems: "center", gap: 5,
            padding: "2px 0", fontSize: 10,
          }}>
            <span style={{ width: 12, textAlign: "center" }}>
              {party.leader === mid ? "👑" : "·"}
            </span>
            <span style={{
              color: mid === uid ? SD.goldBright : SD.starSilver,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              flex: 1,
            }}>{m.name || "???"}</span>
            <span style={{
              fontSize: 7, letterSpacing: 1, color: SD.starDim,
              border: `1px solid ${SD.void4}`, borderRadius: 6,
              padding: "1px 5px", whiteSpace: "nowrap",
            }}>{(GAME_LABELS[m.game] || m.game || "—").toUpperCase()}</span>
          </div>
        ))}
      </div>

      {/* Party chat panel */}
      {chatOpen && (
        <div style={{
          marginTop: 6,
          background: "rgba(5,3,20,0.96)",
          border: `1px solid ${SD.purpleLight}44`,
          borderRadius: 10, padding: "8px 10px",
          boxShadow: `0 0 16px ${SD.purpleLight}22`,
        }}>
          <div style={{ fontSize: 8, color: SD.purpleLight, letterSpacing: 2, marginBottom: 5 }}>
            ♥ PARTY CHAT
          </div>
          <div style={{
            maxHeight: 140, overflowY: "auto",
            display: "flex", flexDirection: "column", gap: 3,
            marginBottom: 6,
          }}>
            {chatMsgs.length === 0 && (
              <div style={{ fontSize: 9, color: SD.starDim, fontStyle: "italic", padding: "6px 0", textAlign: "center" }}>
                Only your party hears this.
              </div>
            )}
            {chatMsgs.map(m => (
              <div key={m.id} style={{ fontSize: 10, lineHeight: 1.45 }}>
                <span style={{ color: m.uid === uid ? SD.goldBright : SD.purpleLight }}>
                  {m.name}:{" "}
                </span>
                <span style={{ color: SD.starSilver, wordBreak: "break-word" }}>{m.text}</span>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <div style={{ display: "flex", gap: 5 }}>
            <input
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => {
                e.stopPropagation();
                if (e.key === "Enter") { e.preventDefault(); sendChat(); }
                if (e.key === "Escape") { e.preventDefault(); setChatOpen(false); }
              }}
              placeholder="To the party…"
              style={{
                flex: 1, minWidth: 0,
                background: "rgba(45,27,105,0.3)",
                border: `1px solid ${SD.purpleLight}44`,
                borderRadius: 12, padding: "5px 10px",
                color: SD.starWhite, fontFamily: SD.fontDisplay,
                fontSize: 10, outline: "none",
              }}
            />
            <button onClick={sendChat} style={{
              background: `linear-gradient(135deg, ${SD.purpleDim}, #3b0f6e)`,
              border: `1px solid ${SD.purpleLight}`,
              borderRadius: 12, padding: "5px 10px",
              color: SD.starWhite, fontSize: 9, fontWeight: 900,
              cursor: "pointer", fontFamily: SD.fontDisplay,
            }}>➤</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PartyInviteBanner — incoming invite, accept/decline
// ─────────────────────────────────────────────────────────────
export function PartyInviteBanner({ invite, acceptInvite, declineInvite }) {
  if (!invite) return null;
  return (
    <div style={{
      position: "fixed", top: 58, left: "50%", transform: "translateX(-50%)",
      zIndex: 220, display: "flex", alignItems: "center", gap: 12,
      background: "rgba(5,3,20,0.97)",
      border: `1px solid ${SD.purpleLight}88`, borderRadius: 12,
      padding: "10px 16px", boxShadow: `0 0 24px ${SD.purpleLight}33`,
      fontFamily: SD.fontDisplay,
    }}>
      <span style={{ fontSize: 11, color: SD.starWhite }}>
        <span style={{ color: SD.purpleLight, fontWeight: 900 }}>{invite.fromName}</span>
        {" "}invites you to their party
      </span>
      <button onClick={acceptInvite} style={{
        background: `linear-gradient(135deg, ${SD.purpleLight}33, ${SD.purpleLight}55)`,
        border: `1px solid ${SD.purpleLight}`, borderRadius: 8,
        padding: "5px 12px", cursor: "pointer",
        color: SD.starWhite, fontSize: 9, letterSpacing: 1, fontWeight: 900,
        fontFamily: SD.fontDisplay,
      }}>♥ JOIN</button>
      <button onClick={declineInvite} style={{
        background: "transparent", border: `1px solid ${SD.void4}`,
        borderRadius: 8, padding: "5px 10px", cursor: "pointer",
        color: SD.starDim, fontSize: 9, letterSpacing: 1,
        fontFamily: SD.fontDisplay,
      }}>✕</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ReadyUpModal — the shared 15-second countdown.
// Everyone sees it. Ready-up early to skip. At 0, everyone goes.
// ─────────────────────────────────────────────────────────────
export function ReadyUpModal({ readyup, party, uid, setReady, cancelReadyUp, isLeader }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(t);
  }, []);

  if (!readyup || !party) return null;
  const color   = readyup.color || SD.gold;
  const secs    = Math.max(0, Math.ceil((readyup.endsAt - now) / 1000));
  const iAmReady = !!(readyup.ready && readyup.ready[uid]);
  const members = Object.entries(party.members || {})
    .sort((a, b) => (a[1].joinedAt || 0) - (b[1].joinedAt || 0));

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 400,
      background: "rgba(0,0,0,0.88)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: SD.fontDisplay,
    }}>
      <div style={{
        background: "rgba(5,3,20,0.98)",
        border: `1px solid ${color}88`,
        borderRadius: 16, padding: "28px 36px",
        width: "min(400px, 92vw)", textAlign: "center",
        boxShadow: `0 0 48px ${color}44`,
      }}>
        <div style={{ fontSize: 44, marginBottom: 8 }}>{readyup.icon}</div>
        <div style={{
          fontSize: 15, fontWeight: 900, color, letterSpacing: 3,
          textShadow: `0 0 14px ${color}`, marginBottom: 4,
        }}>{(readyup.label || "").toUpperCase()}</div>
        <div style={{ fontSize: 10, color: SD.starDim, letterSpacing: 2, marginBottom: 16 }}>
          THE PARTY DEPARTS TOGETHER
        </div>

        <div style={{
          fontSize: 46, fontWeight: 900, color: secs <= 5 ? "#f87171" : SD.starWhite,
          textShadow: `0 0 20px ${secs <= 5 ? "#f87171" : color}66`,
          marginBottom: 16, fontVariantNumeric: "tabular-nums",
        }}>{secs}</div>

        <div style={{
          display: "flex", flexDirection: "column", gap: 5,
          marginBottom: 18, textAlign: "left",
        }}>
          {members.map(([mid, m]) => {
            const rdy = !!(readyup.ready && readyup.ready[mid]);
            return (
              <div key={mid} style={{
                display: "flex", alignItems: "center", gap: 8,
                fontSize: 11, padding: "3px 8px",
                background: rdy ? `${color}14` : "transparent",
                border: `1px solid ${rdy ? color + "55" : SD.void4}`,
                borderRadius: 8,
              }}>
                <span>{rdy ? "✅" : "⏳"}</span>
                <span style={{
                  color: mid === uid ? SD.goldBright : SD.starSilver, flex: 1,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {party.leader === mid ? "👑 " : ""}{m.name || "???"}
                </span>
                <span style={{ fontSize: 8, letterSpacing: 1, color: rdy ? color : SD.starDim }}>
                  {rdy ? "READY" : "…"}
                </span>
              </div>
            );
          })}
        </div>

        {!iAmReady ? (
          <button onClick={setReady} style={{
            width: "100%", padding: "13px",
            background: `linear-gradient(135deg, ${color}33, ${color}55)`,
            border: `1px solid ${color}`, borderRadius: 8, cursor: "pointer",
            color, fontSize: 13, fontWeight: 900, letterSpacing: 3,
            fontFamily: SD.fontDisplay,
            boxShadow: `0 0 20px ${color}44`, marginBottom: 10,
          }}>◆ READY UP</button>
        ) : (
          <div style={{
            padding: "13px", borderRadius: 8, marginBottom: 10,
            border: `1px solid ${color}66`, color,
            fontSize: 11, letterSpacing: 2, fontWeight: 900,
          }}>READY — WAITING FOR THE OTHERS</div>
        )}

        {(isLeader || readyup.startedBy === uid) && (
          <button onClick={cancelReadyUp} style={{
            width: "100%", padding: "9px",
            background: "transparent", border: `1px solid ${SD.void4}`,
            borderRadius: 8, cursor: "pointer",
            color: SD.starDim, fontSize: 9, letterSpacing: 2,
            fontFamily: SD.fontDisplay,
          }}>CANCEL THE LAUNCH</button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SoloInvitePill — the locked Phase-1 UI rule:
// when solo, the "invite friends" pill is persistent, never hidden.
// ─────────────────────────────────────────────────────────────
export function SoloInvitePill({ bottom = 20, right = 20 }) {
  const [hint, setHint] = useState(false);
  return (
    <div style={{
      position: "fixed", bottom, right, zIndex: 150,
      display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6,
      fontFamily: SD.fontDisplay,
    }}>
      {hint && (
        <div style={{
          background: "rgba(5,3,20,0.96)",
          border: `1px solid ${SD.purpleLight}44`,
          borderRadius: 10, padding: "8px 12px",
          fontSize: 10, color: SD.starSilver, lineHeight: 1.5,
          maxWidth: 210, textAlign: "right",
          boxShadow: `0 0 14px ${SD.purpleLight}22`,
        }}>
          Walk up to a fellow wanderer and press{" "}
          <span style={{ color: SD.purpleLight, fontWeight: 900 }}>♥ PARTY UP</span>.
          Parties travel every portal together.
        </div>
      )}
      <button onClick={() => setHint(h => !h)} style={{
        background: `linear-gradient(135deg, ${SD.purpleLight}22, rgba(4,2,14,0.95))`,
        border: `1px solid ${SD.purpleLight}66`, borderRadius: 20,
        padding: "8px 14px", cursor: "pointer",
        color: SD.purpleLight, fontSize: 9, letterSpacing: 2, fontWeight: 900,
        fontFamily: SD.fontDisplay,
        boxShadow: `0 0 12px ${SD.purpleLight}22`, whiteSpace: "nowrap",
      }}>♥ INVITE FRIENDS</button>
    </div>
  );
}
